import { CHAT_FILTERS } from "../constants/chatV2Constants";

/**
 * Map ChatV2 nav filter id → ChatBar controlled view.
 * @param {string} filterId
 * @returns {"Chats"|"UnAssigned"|"Archive"}
 */
export function filterIdToView(filterId) {
  const f = CHAT_FILTERS.find((x) => x.id === filterId);
  return f?.view || "Chats";
}

/**
 * Map URL `?tab=` value → ChatV2 filter id.
 * Accepts both `closed` (ChatV2) and `archive` (legacy/constants alias).
 * @param {string|null|undefined} tab
 * @returns {"all"|"unassigned"|"closed"}
 */
export function filterIdFromTab(tab) {
  const t = (tab || "").toLowerCase();
  if (t === "unassigned") return "unassigned";
  if (t === "archive" || t === "closed") return "closed";
  return "all";
}

/**
 * Map ChatBar view → ChatV2 filter id (inverse of filterIdToView).
 * @param {string} view
 * @returns {"all"|"unassigned"|"closed"}
 */
export function filterIdFromView(view) {
  if (view === "UnAssigned") return "unassigned";
  if (view === "Archive") return "closed";
  return "all";
}

/**
 * Path used when selecting a ChatV2 filter (keeps Closed as `tab=closed`).
 * @param {string} filterId
 * @returns {string}
 */
export function chatPathForFilter(filterId) {
  const view = filterIdToView(filterId);
  if (view === "UnAssigned") return "/chat?tab=unassigned";
  if (view === "Archive") return "/chat?tab=closed";
  return "/chat";
}

/**
 * Badge count for a ChatV2 nav filter.
 * @param {string} id
 * @param {Record<string, number>|null|undefined} counts
 * @returns {number}
 */
export function filterCount(id, counts) {
  const c = counts || {};
  if (id === "all") return c.all ?? c.assigned_to_me ?? 0;
  if (id === "unassigned") return c.unassigned ?? 0;
  if (id === "closed") return c.closed ?? 0;
  return 0;
}

/**
 * Merge list counts from ChatBar; All prefers assigned_to_me.
 * @param {Record<string, number>} prev
 * @param {Record<string, number>} counts
 * @returns {Record<string, number>}
 */
export function mergeChatCounts(prev, counts) {
  return {
    ...prev,
    ...counts,
    all: counts.assigned_to_me ?? counts.all ?? prev.all ?? 0,
  };
}

/**
 * Parse name/email from the first visitor message profile blob.
 * @param {unknown[]} messages
 * @returns {{ name?: string, email?: string } | null}
 */
export function parseVisitorProfileFromMessages(messages) {
  if (!Array.isArray(messages) || messages.length === 0) return null;
  const firstVisitorMessage = messages.find(
    (msg) => !msg?.isAgent && typeof msg?.content === "string"
  );
  if (!firstVisitorMessage?.content) return null;

  const lines = firstVisitorMessage.content
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
  const profile = {};

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    if (line.includes(":")) {
      const [rawKey, ...rest] = line.split(":");
      const key = rawKey?.trim()?.toLowerCase();
      const inlineValue = rest.join(":").trim();
      const nextValue =
        lines[i + 1] && !lines[i + 1].includes(":") ? lines[i + 1] : "";
      const value = inlineValue || nextValue;
      if (!value) continue;
      if (key.includes("name")) profile.name = value;
      if (key.includes("email")) profile.email = value;
    }
  }

  if (!profile.name && !profile.email) return null;
  return profile;
}

/**
 * List timestamp: today → clock; yesterday → "Yesterday"; older → DD/MM/YYYY.
 * @param {string|null|undefined} time
 * @param {string|null|undefined} iso
 * @returns {string}
 */
export function formatCv2ListTime(time, iso) {
  if (time == null && iso == null) return "";
  if (time === "" || time === "—") return "";

  const raw = time != null ? String(time).trim() : "";
  if (/^now$/i.test(raw)) return "Now";

  let d = null;
  if (iso != null && iso !== "") {
    const fromIso = new Date(iso);
    if (!Number.isNaN(fromIso.getTime())) d = fromIso;
  }
  if (!d && raw) {
    const candidate = raw.includes(" - ") ? raw.split(" - ").pop() : raw;
    const parsed = new Date(candidate);
    if (!Number.isNaN(parsed.getTime())) d = parsed;
  }

  if (!d || Number.isNaN(d.getTime())) {
    const clockMatch = raw.match(/(\d{1,2}:\d{2}\s*[ap]m)/i);
    return clockMatch ? clockMatch[1].toLowerCase() : raw;
  }

  const now = new Date();
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  );
  const startOfDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffDays = Math.round((startOfToday - startOfDate) / 86400000);

  if (diffDays <= 0) {
    return d
      .toLocaleTimeString(undefined, {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
      .toLowerCase();
  }

  if (diffDays === 1) return "Yesterday";

  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  return `${day}/${month}/${d.getFullYear()}`;
}
